/**
 * Header - Cabecera de la aplicación
 */

import { useMemo } from 'react';
import { BrainCircuit, Settings, RefreshCw, BarChart3, LayoutDashboard, Backpack, Timer, Focus, Trophy } from 'lucide-react';
import { useProtocolStore, useUIStore } from '@/store';
import { calculateDayProgress } from '@/core/utils';
import { cn } from '@/core/utils';
import { ProgressRing, Button, Tooltip } from '@/ui/primitives';
import { useConfirm, ConfirmDialog } from '@/ui/composed';
import { trackFeatureUsed } from '@/services/achievements';

export function Header() {
  const protocols = useProtocolStore((state) => state.protocols);
  const activeFrequency = useProtocolStore((state) => state.activeFrequency);
  const resetByFrequency = useProtocolStore((state) => state.resetByFrequency);
  const openModal = useUIStore((state) => state.openModal);
  const activeView = useUIStore((state) => state.activeView);
  const setActiveView = useUIStore((state) => state.setActiveView);
  const setFocusMode = useUIStore((state) => state.setFocusMode);

  // Calcular progreso con useMemo para evitar re-renders infinitos
  const dayProgress = useMemo(
    () => calculateDayProgress(protocols, activeFrequency),
    [protocols, activeFrequency]
  );

  const { requestConfirm: requestReset, dialogProps: resetDialogProps } = useConfirm({
    onConfirm: () => resetByFrequency(activeFrequency),
    title: '¿Resetear tareas?',
    description: 'Esto marcara todas las tareas de hoy como no completadas.',
    confirmLabel: 'Si, resetear',
    cancelLabel: 'Cancelar',
    variant: 'warning',
    reassurance: 'Es normal necesitar reiniciar. Puedes volver a completarlas despues.',
  });

  // Determinar la capa dominante basada en el progreso
  const getDominantLayer = () => {
    if (!dayProgress.survivalComplete) return 'survival';
    if (!dayProgress.maintenanceComplete) return 'maintenance';
    return 'growth';
  };

  return (
    <header className="sticky top-0 z-50 safe-top">
      <div className="exo-glass border-b border-slate-800/50">
        <div className="exo-container py-3">
          {/* Fila 1: Logo + Progreso + Acciones */}
          <div className="flex items-center justify-between">
            {/* Logo y título */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-xl bg-slate-800">
                <BrainCircuit className="w-5 h-5 sm:w-6 sm:h-6 text-slate-300" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-100">Exocortex</h1>
                <p className="text-2xs text-slate-500 hidden sm:block">Sistema Auxiliar Cognitivo</p>
              </div>
            </div>

            {/* Progreso y acciones */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Tooltip content={`Progreso del dia: ${dayProgress.percentage}%`} position="bottom">
                <ProgressRing
                  value={dayProgress.percentage}
                  size={36}
                  strokeWidth={3}
                  layer={getDominantLayer()}
                />
              </Tooltip>

              <div className="flex items-center gap-1">
                {activeView === 'dashboard' && (
                  <>
                    <Tooltip content="Modo focus" position="bottom">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFocusMode(true);
                          trackFeatureUsed('focus');
                        }}
                        aria-label="Modo focus"
                      >
                        <Focus className="w-4 h-4" />
                      </Button>
                    </Tooltip>

                    <Tooltip content="Resetear tareas del dia" position="bottom">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={requestReset}
                        aria-label="Resetear tareas"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                  </>
                )}

                <Tooltip content="Configuracion" position="bottom">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openModal('settings')}
                    aria-label="Configuración"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Fila 2: Navegación */}
          <nav className="mt-3 -mx-1 overflow-x-auto scrollbar-hide sm:flex sm:justify-center">
            <div className="flex gap-1 p-1 bg-slate-900 rounded-lg w-fit min-w-full sm:w-auto sm:min-w-0">
              <button
                onClick={() => setActiveView('dashboard')}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial whitespace-nowrap',
                  activeView === 'dashboard'
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Hoy</span>
              </button>
              <button
                onClick={() => setActiveView('pack')}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial whitespace-nowrap',
                  activeView === 'pack'
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <Backpack className="w-4 h-4" />
                <span>Pack</span>
              </button>
              <button
                onClick={() => setActiveView('timer')}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial whitespace-nowrap',
                  activeView === 'timer'
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <Timer className="w-4 h-4" />
                <span>Timer</span>
              </button>
              <button
                onClick={() => setActiveView('stats')}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial whitespace-nowrap',
                  activeView === 'stats'
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Stats</span>
              </button>
              <button
                onClick={() => setActiveView('achievements')}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial whitespace-nowrap',
                  activeView === 'achievements'
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <Trophy className="w-4 h-4" />
                <span>Logros</span>
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Dialog de confirmacion para reset */}
      <ConfirmDialog {...resetDialogProps} />
    </header>
  );
}
